import { isFulfilled } from "@reduxjs/toolkit";
import { useContext } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NO_DRAG_SELECTOR } from "../../../../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { CustomButton } from "../../../../../../common/components/functional/customButton/CustomButton";
import OverlaySpinner from "../../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import { AccessRightContext } from "../../../../../user/AccessRightProvider";
import { CustomerContract } from "../../../../customerAPI";
import { updateContractPricing } from "../../../../customerContractsSlice";
import { ActionButtonsContainer, ModalContainer } from "../../../../styles";
import {
  getPricingGeneralSettingsDefaultValues,
  preparePricingGeneralSettingsForApi,
  PricingGeneralSettings,
} from "../../../helpers/pricingUtils";
import { StyledFormGroup } from "../../../styles";
import PricingFormModalHeader from "../common/PricingFormModalHeader";
import BillingSupplementSection from "./BillingSupplementSection";
import { UnsavedChangesProvider } from "../../../../../../contexts/UnsavedChangesContext";

export interface PricingGeneralSettingsData {
  pricingValidFromDate: string;
  pricingGeneralSettings?: PricingGeneralSettings;
  existingPricingObjectFromApi?: any;
}

interface Props {
  openedFromPricingContent?: boolean;
  contractData: CustomerContract;
  pricingGeneralSettingsData: PricingGeneralSettingsData;
  eezyCompanyName: string;
  closeModal: () => void;
}

const PricingGeneralSettingsForm = ({
  openedFromPricingContent,
  contractData,
  pricingGeneralSettingsData,
  eezyCompanyName,
  closeModal,
}: Props) => {
  const { t } = useTranslation("customers");
  const { setValue: setMainFormValue } = useFormContext() || {};
  const dispatch = useAppDispatch();

  const { canEditContract } = useContext(AccessRightContext);

  const { status } = useAppSelector((state) => state.customerContracts);
  const isLoading = status === "pending";

  const {
    pricingValidFromDate,
    pricingGeneralSettings,
    existingPricingObjectFromApi,
  } = pricingGeneralSettingsData;

  const defaultValues = getPricingGeneralSettingsDefaultValues(
    pricingGeneralSettings,
  );

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting, errors },
  } = methods;

  const onSubmit = (formData: any) => {
    const updatedPricingGeneralSettings =
      preparePricingGeneralSettingsForApi(formData);

    if (openedFromPricingContent) {
      // Update only main form
      setMainFormValue(
        "pricingGeneralSettings",
        updatedPricingGeneralSettings,
        {
          shouldDirty: true,
        },
      );
      closeModal();
    } else {
      // Update whole contract pricing with new general settings
      const updatedPricing = {
        id: contractData.id,
        version: contractData.version,
        validFrom: pricingValidFromDate,
        pricing: {
          ...existingPricingObjectFromApi,
          ...updatedPricingGeneralSettings,
        },
      };

      dispatch(updateContractPricing(updatedPricing)).then((action) => {
        if (isFulfilled(action)) closeModal();
      });
    }
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    reset(defaultValues);
  };

  const handleClose = () => {
    onCancel();
    closeModal();
  };

  const watchedValues = watch();

  const areValuesValid =
    !isEmpty(watchedValues.invoiceFee) &&
    !isEmpty(watchedValues.creditInvoiceFee);

  const saveDisabled =
    !isDirty ||
    !areValuesValid ||
    !isEmpty(errors) ||
    isSubmitting ||
    isLoading;

  return (
    <ModalContainer>
      <PricingFormModalHeader
        pricingValidFromDate={pricingValidFromDate}
        selectedItemName={t("contract.generalPricingAlt")}
        workLocationName={eezyCompanyName}
        handleClose={handleClose}
      />
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <StyledFormGroup className={NO_DRAG_SELECTOR}>
            <OverlaySpinner isLoading={isLoading} />
            <BillingSupplementSection />
          </StyledFormGroup>
        </UnsavedChangesProvider>
      </FormProvider>
      <ActionButtonsContainer marginTop="1rem">
        <CustomButton
          accessRightChecks={[canEditContract(contractData.eezyCompany.id)]}
          variant="contained"
          sx={{ borderRadius: "15px", height: "30px" }}
          onClick={onSave}
          disabled={saveDisabled}
        >
          {openedFromPricingContent ? t("common:complete") : t("common:save")}
        </CustomButton>
      </ActionButtonsContainer>
    </ModalContainer>
  );
};

export default PricingGeneralSettingsForm;
